import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, CheckSquare, ListTodo, Eye, FileText, Loader2 } from 'lucide-react';
import { projectService } from '../../services/projectService';
import { clientService } from '../../services/clientService';
import { userService } from '../../services/userService';
import { useToast } from '../../hooks/use-toast';

const Projetos = () => {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [subtasksDialogOpen, setSubtasksDialogOpen] = useState(false);
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(null); // armazena ID do projeto sendo exportado
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'media',
    progress: 0,
    start_date: '',
    end_date: '',
    client_id: '',
    responsible_id: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    status: 'novo',
    priority: 'media',
    progress: 0,
    start_date: '',
    end_date: '',
    responsible_id: ''
  });
  const [subtaskFormData, setSubtaskFormData] = useState({
    title: '',
    description: '',
    priority: 'media',
    responsible_id: '',
    start_date: '',
    end_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsData, clientsData, usersData] = await Promise.all([
        projectService.getAll(),
        clientService.getAll(),
        userService.getAll()
      ]);
      setProjects(projectsData);
      setClients(clientsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast({
        title: 'Erro ao carregar projetos',
        description: 'Não foi possível carregar os projetos.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client ? client.name : 'Desconhecido';
  };

  const getUserName = (userId) => {
    if (!userId) return 'Não atribuído';
    const user = users.find(u => u._id === userId);
    return user ? user.name : 'Desconhecido';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'novo': return 'bg-yellow-100 text-yellow-800';
      case 'em_andamento': return 'bg-blue-100 text-blue-800';
      case 'concluido': return 'bg-green-100 text-green-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta': return 'bg-red-100 text-red-800';
      case 'media': return 'bg-yellow-100 text-yellow-800';
      case 'baixa': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data, removing empty dates
      const projectData = { ...formData };
      if (!projectData.start_date) delete projectData.start_date;
      if (!projectData.end_date) delete projectData.end_date;
      if (!projectData.responsible_id) delete projectData.responsible_id;
      
      await projectService.create(projectData);
      toast({
        title: 'Projeto criado',
        description: 'O projeto foi criado com sucesso.',
      });
      setDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        priority: 'media',
        progress: 0,
        start_date: '',
        end_date: '',
        client_id: '',
        responsible_id: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível criar o projeto.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setEditFormData({
      name: project.name,
      description: project.description || '',
      status: project.status,
      priority: project.priority,
      progress: project.progress,
      start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
      end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : '',
      responsible_id: project.responsible_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...editFormData };
      if (!updateData.start_date) delete updateData.start_date;
      if (!updateData.end_date) delete updateData.end_date;
      if (!updateData.responsible_id) delete updateData.responsible_id;
      
      await projectService.update(editingProject._id, updateData);
      toast({
        title: 'Projeto atualizado',
        description: 'O projeto foi atualizado com sucesso.',
      });
      setEditDialogOpen(false);
      setEditingProject(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar projeto:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível atualizar o projeto.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto? Todas as subtarefas também serão excluídas.')) return;
    
    try {
      await projectService.delete(projectId);
      toast({
        title: 'Projeto excluído',
        description: 'O projeto foi excluído com sucesso.',
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir projeto:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o projeto.',
        variant: 'destructive',
      });
    }
  };

  const handleViewSubtasks = async (project) => {
    setSelectedProject(project);
    try {
      const subtasksData = await projectService.getSubtasks(project._id);
      setSubtasks(subtasksData);
      setSubtasksDialogOpen(true);
    } catch (error) {
      console.error('Erro ao buscar subtarefas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as subtarefas.',
        variant: 'destructive',
      });
    }
  };

  const handleViewDetails = async (project) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/projetos/${project._id}/detalhes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const details = await response.json();
      setProjectDetails(details);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os detalhes do projeto.',
        variant: 'destructive',
      });
    }
  };

  const handleExportPDF = async (projectId) => {
    if (!projectId) {
      toast({
        title: "Erro",
        description: "ID do projeto não encontrado.",
        variant: "destructive",
      });
      return;
    }
    
    setExportingPdf(projectId);
    try {
      await projectService.exportProjectPDF(projectId);
      toast({
        title: "Sucesso!",
        description: "Relatório do projeto exportado com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro ao exportar",
        description: "Não foi possível gerar o relatório PDF. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setExportingPdf(null);
    }
  };

  const handleAddSubtask = () => {
    setEditingSubtask(null);
    setSubtaskFormData({
      title: '',
      description: '',
      priority: 'media',
      order: subtasks.length,
      responsible_id: '',
      start_date: '',
      end_date: '',
      notes: ''
    });
    setSubtaskDialogOpen(true);
  };

  const handleEditSubtask = (subtask) => {
    setEditingSubtask(subtask);
    setSubtaskFormData({
      title: subtask.title,
      description: subtask.description || '',
      priority: subtask.priority,
      order: subtask.order,
      responsible_id: subtask.responsible_id || '',
      start_date: subtask.start_date ? new Date(subtask.start_date).toISOString().split('T')[0] : '',
      end_date: subtask.end_date ? new Date(subtask.end_date).toISOString().split('T')[0] : '',
      notes: subtask.notes || '',
      status: subtask.status
    });
    setSubtaskDialogOpen(true);
  };

  const handleSubtaskSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProject?._id) {
      toast({
        title: 'Erro',
        description: 'Projeto não encontrado.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const data = { ...subtaskFormData };
      if (!data.start_date) delete data.start_date;
      if (!data.end_date) delete data.end_date;
      if (!data.responsible_id) delete data.responsible_id;
      
      if (editingSubtask) {
        await projectService.updateSubtask(selectedProject._id, editingSubtask._id, data);
        toast({
          title: 'Subtarefa atualizada',
          description: 'A subtarefa foi atualizada com sucesso.',
        });
      } else {
        delete data.status; // Status is set by backend for new subtasks
        await projectService.createSubtask(selectedProject._id, data);
        toast({
          title: 'Subtarefa criada',
          description: 'A subtarefa foi criada com sucesso.',
        });
      }
      
      setSubtaskDialogOpen(false);
      // Refresh subtasks
      const subtasksData = await projectService.getSubtasks(selectedProject._id);
      setSubtasks(subtasksData);
    } catch (error) {
      console.error('Erro ao salvar subtarefa:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível salvar a subtarefa.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (!selectedProject?._id) {
      toast({
        title: 'Erro',
        description: 'Projeto não encontrado.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!window.confirm('Tem certeza que deseja excluir esta subtarefa?')) return;
    
    try {
      await projectService.deleteSubtask(selectedProject._id, subtaskId);
      toast({
        title: 'Subtarefa excluída',
        description: 'A subtarefa foi excluída com sucesso.',
      });
      // Refresh subtasks
      const subtasksData = await projectService.getSubtasks(selectedProject._id);
      setSubtasks(subtasksData);
    } catch (error) {
      console.error('Erro ao excluir subtarefa:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir a subtarefa.',
        variant: 'destructive',
      });
    }
  };

  const getSubtaskStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'bg-gray-100 text-gray-800';
      case 'em_andamento': return 'bg-blue-100 text-blue-800';
      case 'concluida': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredProjects = projects.filter(project => {
    if (filterClient !== 'all' && project.client_id !== filterClient) return false;
    if (filterStatus !== 'all' && project.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projetos</h1>
          <p className="text-gray-600 mt-1">Gerencie todos os projetos em andamento</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
              <Plus className="w-4 h-4 mr-2" />
              Novo Projeto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Novo Projeto</DialogTitle>
              <DialogDescription>Crie um novo projeto de TI</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Projeto *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Cliente *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client._id} value={client._id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="responsible_id">Responsável</Label>
                  <Select
                    value={formData.responsible_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, responsible_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o responsável" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não atribuído</SelectItem>
                      {users.filter(u => u.role === 'tecnico' || u.role === 'admin').map((user) => (
                        <SelectItem key={user._id} value={user._id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="priority">Prioridade</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="media">Média</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="progress">Progresso (%)</Label>
                  <Input
                    id="progress"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.progress}
                    onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Data de Início</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Previsão de Término</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
                  Criar Projeto
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <Label htmlFor="filter-client" className="text-sm font-medium">
            Filtrar por Cliente
          </Label>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger id="filter-client" className="mt-1">
              <SelectValue placeholder="Todos os clientes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os clientes</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client._id} value={client._id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex-1">
          <Label htmlFor="filter-status" className="text-sm font-medium">
            Filtrar por Status
          </Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger id="filter-status" className="mt-1">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="novo">Novo</SelectItem>
              <SelectItem value="em_andamento">Em Andamento</SelectItem>
              <SelectItem value="concluido">Concluído</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-end">
          <Button 
            variant="outline" 
            onClick={() => {
              setFilterClient('all');
              setFilterStatus('all');
            }}
            className="mt-1"
          >
            Limpar Filtros
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-gray-500">
            Nenhum projeto cadastrado ainda
          </CardContent>
        </Card>
      ) : filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-gray-500">
            Nenhum projeto encontrado com os filtros aplicados
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredProjects.map((project) => (
            <Card key={project._id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle>{project.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {getClientName(project.client_id)}
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={getStatusColor(project.status)}>
                      {project.status === 'novo' ? 'Novo' : project.status === 'em_andamento' ? 'Em Andamento' : project.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.description && (
                  <p className="text-sm text-gray-600">{project.description}</p>
                )}
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progresso</span>
                    <span className="font-semibold">{project.progress}%</span>
                  </div>
                  <Progress value={project.progress} className="h-2" />
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Responsável</p>
                    <p className="font-medium">{getUserName(project.responsible_id)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Prioridade</p>
                    <Badge className={getPriorityColor(project.priority)}>
                      {project.priority.charAt(0).toUpperCase() + project.priority.slice(1)}
                    </Badge>
                  </div>
                  {project.start_date && (
                    <div>
                      <p className="text-gray-600">Data Início</p>
                      <p className="font-medium">
                        {new Date(project.start_date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  )}
                  {project.end_date && (
                    <div>
                      <p className="text-gray-600">Previsão</p>
                      <p className="font-medium">
                        {new Date(project.end_date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2 pt-4 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDetails(project)}
                    className="flex-1 border-[#4A2B7F] text-[#4A2B7F] hover:bg-[#4A2B7F] hover:text-white"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Detalhes
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewSubtasks(project)}
                    className="flex-1"
                  >
                    <ListTodo className="w-4 h-4 mr-2" />
                    Subtarefas
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(project)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(project._id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Projeto</DialogTitle>
            <DialogDescription>Atualize as informações do projeto</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Nome do Projeto *</Label>
              <Input
                id="edit_name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_description">Descrição</Label>
              <Textarea
                id="edit_description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_status">Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="novo">Novo</SelectItem>
                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_responsible_id">Responsável</Label>
                <Select
                  value={editFormData.responsible_id || 'none'}
                  onValueChange={(value) => setEditFormData({ ...editFormData, responsible_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o responsável" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não atribuído</SelectItem>
                    {users.filter(u => u.role === 'tecnico' || u.role === 'admin').map((user) => (
                      <SelectItem key={user._id} value={user._id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_priority">Prioridade</Label>
                <Select
                  value={editFormData.priority}
                  onValueChange={(value) => setEditFormData({ ...editFormData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_progress">Progresso (%)</Label>
                <Input
                  id="edit_progress"
                  type="number"
                  min="0"
                  max="100"
                  value={editFormData.progress}
                  onChange={(e) => setEditFormData({ ...editFormData, progress: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_start_date">Data de Início</Label>
                <Input
                  id="edit_start_date"
                  type="date"
                  value={editFormData.start_date}
                  onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_end_date">Previsão de Término</Label>
                <Input
                  id="edit_end_date"
                  type="date"
                  value={editFormData.end_date}
                  onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
                Atualizar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subtasks Dialog */}
      <Dialog open={subtasksDialogOpen} onOpenChange={setSubtasksDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Subtarefas - {selectedProject?.name}
            </DialogTitle>
            <DialogDescription>
              Gerencie as atividades do projeto
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Button
              onClick={handleAddSubtask}
              className="w-full bg-[#4A2B7F] hover:bg-[#6B46C1]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Subtarefa
            </Button>

            {subtasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma subtarefa cadastrada
              </div>
            ) : (
              <div className="space-y-3">
                {subtasks.map((subtask) => (
                  <Card key={subtask._id} className="border-l-4 border-l-[#4A2B7F]">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold">{subtask.title}</h4>
                            <Badge className={getSubtaskStatusColor(subtask.status)}>
                              {subtask.status === 'pendente' ? 'Pendente' : subtask.status === 'em_andamento' ? 'Em Andamento' : 'Concluída'}
                            </Badge>
                            <Badge className={getPriorityColor(subtask.priority)}>
                              {subtask.priority.charAt(0).toUpperCase() + subtask.priority.slice(1)}
                            </Badge>
                          </div>
                          {subtask.description && (
                            <p className="text-sm text-gray-600 mb-2">{subtask.description}</p>
                          )}
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {subtask.responsible_id && (
                              <div>
                                <span className="text-gray-600">Responsável:</span>{' '}
                                <span className="font-medium">{getUserName(subtask.responsible_id)}</span>
                              </div>
                            )}
                            {subtask.start_date && (
                              <div>
                                <span className="text-gray-600">Início:</span>{' '}
                                <span className="font-medium">
                                  {new Date(subtask.start_date).toLocaleDateString('pt-BR')}
                                </span>
                              </div>
                            )}
                            {subtask.end_date && (
                              <div>
                                <span className="text-gray-600">Término:</span>{' '}
                                <span className="font-medium">
                                  {new Date(subtask.end_date).toLocaleDateString('pt-BR')}
                                </span>
                              </div>
                            )}
                          </div>
                          {subtask.notes && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">Notas:</span> {subtask.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => handleEditSubtask(subtask)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteSubtask(subtask._id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Subtask Dialog */}
      <Dialog open={subtaskDialogOpen} onOpenChange={setSubtaskDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSubtask ? 'Editar Subtarefa' : 'Nova Subtarefa'}</DialogTitle>
            <DialogDescription>
              {editingSubtask ? 'Atualize as informações da subtarefa' : 'Adicione uma nova subtarefa ao projeto'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubtaskSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subtask_title">Título *</Label>
              <Input
                id="subtask_title"
                value={subtaskFormData.title}
                onChange={(e) => setSubtaskFormData({ ...subtaskFormData, title: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtask_description">Descrição</Label>
              <Textarea
                id="subtask_description"
                value={subtaskFormData.description}
                onChange={(e) => setSubtaskFormData({ ...subtaskFormData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {editingSubtask && (
                <div className="space-y-2">
                  <Label htmlFor="subtask_status">Status</Label>
                  <Select
                    value={subtaskFormData.status}
                    onValueChange={(value) => setSubtaskFormData({ ...subtaskFormData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="concluida">Concluída</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="subtask_priority">Prioridade</Label>
                <Select
                  value={subtaskFormData.priority}
                  onValueChange={(value) => setSubtaskFormData({ ...subtaskFormData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtask_responsible">Responsável</Label>
              <Select
                value={subtaskFormData.responsible_id || 'none'}
                onValueChange={(value) => setSubtaskFormData({ ...subtaskFormData, responsible_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o responsável" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não atribuído</SelectItem>
                  {users.filter(u => u.role === 'tecnico' || u.role === 'admin').map((user) => (
                    <SelectItem key={user._id} value={user._id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subtask_start_date">Data de Início</Label>
                <Input
                  id="subtask_start_date"
                  type="date"
                  value={subtaskFormData.start_date}
                  onChange={(e) => setSubtaskFormData({ ...subtaskFormData, start_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subtask_end_date">Previsão de Término</Label>
                <Input
                  id="subtask_end_date"
                  type="date"
                  value={subtaskFormData.end_date}
                  onChange={(e) => setSubtaskFormData({ ...subtaskFormData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtask_notes">Notas</Label>
              <Textarea
                id="subtask_notes"
                value={subtaskFormData.notes}
                onChange={(e) => setSubtaskFormData({ ...subtaskFormData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setSubtaskDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
                {editingSubtask ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Detalhes do Projeto</DialogTitle>
            <DialogDescription>Visualização completa do projeto e suas subtarefas</DialogDescription>
          </DialogHeader>

          {projectDetails && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{projectDetails.name}</CardTitle>
                  <CardDescription>
                    {projectDetails.client_info?.name || 'Cliente não especificado'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {projectDetails.description && (
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Descrição</h4>
                      <p className="text-sm text-gray-600">{projectDetails.description}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Status</p>
                      <Badge className={getStatusColor(projectDetails.status)}>
                        {projectDetails.status === 'novo' ? 'Novo' : projectDetails.status === 'em_andamento' ? 'Em Andamento' : projectDetails.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-gray-600">Prioridade</p>
                      <Badge className={getPriorityColor(projectDetails.priority)}>
                        {projectDetails.priority.charAt(0).toUpperCase() + projectDetails.priority.slice(1)}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-gray-600">Progresso</p>
                      <p className="font-medium">{projectDetails.progress}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Responsável</p>
                      <p className="font-medium text-xs">{projectDetails.responsible_info?.name || 'Não atribuído'}</p>
                    </div>
                  </div>

                  {(projectDetails.start_date || projectDetails.end_date) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {projectDetails.start_date && (
                        <div>
                          <p className="text-gray-600">Data Início</p>
                          <p className="font-medium">
                            {new Date(projectDetails.start_date).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      )}
                      {projectDetails.end_date && (
                        <div>
                          <p className="text-gray-600">Previsão de Término</p>
                          <p className="font-medium">
                            {new Date(projectDetails.end_date).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <p className="text-gray-600 text-sm mb-2">Progresso</p>
                    <Progress value={projectDetails.progress} className="h-3" />
                  </div>
                </CardContent>
              </Card>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    Subtarefas ({projectDetails.completed_subtasks}/{projectDetails.subtasks_count})
                  </h3>
                </div>

                {projectDetails.subtasks && projectDetails.subtasks.length > 0 ? (
                  <div className="space-y-3">
                    {projectDetails.subtasks.map((subtask) => (
                      <Card key={subtask._id} className="border-l-4 border-l-[#4A2B7F]">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="font-bold text-sm text-gray-500">#{subtask.order}</span>
                                <h4 className="font-semibold">{subtask.title}</h4>
                                <Badge className={getSubtaskStatusColor(subtask.status)}>
                                  {subtask.status === 'pendente' ? 'Pendente' : subtask.status === 'em_andamento' ? 'Em Andamento' : 'Concluída'}
                                </Badge>
                                <Badge className={getPriorityColor(subtask.priority)}>
                                  {subtask.priority.charAt(0).toUpperCase() + subtask.priority.slice(1)}
                                </Badge>
                              </div>
                              {subtask.description && (
                                <p className="text-sm text-gray-600 mb-2">{subtask.description}</p>
                              )}
                              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                                <div>
                                  <span className="font-medium">Responsável:</span> {subtask.responsible_name || 'Não atribuído'}
                                </div>
                                {subtask.start_date && (
                                  <div>
                                    <span className="font-medium">Início:</span> {new Date(subtask.start_date).toLocaleDateString('pt-BR')}
                                  </div>
                                )}
                                {subtask.end_date && (
                                  <div>
                                    <span className="font-medium">Término:</span> {new Date(subtask.end_date).toLocaleDateString('pt-BR')}
                                  </div>
                                )}
                              </div>
                              {subtask.notes && (
                                <div className="mt-2 text-xs text-gray-600">
                                  <span className="font-medium">Notas:</span> {subtask.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="text-center py-6 text-gray-500">
                      Nenhuma subtarefa cadastrada
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {projectDetails && (
            <DialogFooter className="mt-6">
              <Button
                onClick={() => projectDetails?._id && handleExportPDF(projectDetails._id)}
                disabled={!projectDetails?._id || exportingPdf === projectDetails._id}
                variant="outline"
                className="gap-2"
              >
                {exportingPdf === projectDetails._id ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Gerando PDF...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Exportar PDF
                  </>
                )}
              </Button>
              <Button onClick={() => setDetailsDialogOpen(false)} variant="secondary">
                Fechar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projetos;